import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import { ShieldCheck } from 'lucide-react'

export default function LoginPage() {
  const navigate = useNavigate()
  const setSession = useAuthStore((s) => s.setSession)
  const setIsPlatformAdmin = useAuthStore((s) => s.setIsPlatformAdmin)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      // Sign out locally first to clear any stale/in-progress session state
      // (scope: 'local' avoids a server round-trip).
      // This prevents signInWithPassword from hanging when Supabase client has
      // an ongoing token refresh cycle from a previous expired session.
      await supabase.auth.signOut({ scope: 'local' })

      // Race against a 15 s timeout to prevent infinite loading
      const loginPromise = supabase.auth.signInWithPassword({ email, password })
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Login timeout. Cek koneksi dan coba lagi.')), 15000)
      )
      const { data, error: authError } = await Promise.race([loginPromise, timeoutPromise])

      if (authError || !data.session) {
        setError('Email atau password salah.')
        return
      }

      // Check if this user is a platform admin
      const { data: adminRow } = await supabase
        .from('platform_admins')
        .select('id')
        .eq('user_id', data.user.id)
        .maybeSingle()

      if (!adminRow) {
        await supabase.auth.signOut()
        setError('Akun ini tidak memiliki akses admin platform.')
        return
      }

      setSession(data.session)
      setIsPlatformAdmin(true)
      navigate('/', { replace: true })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-sm space-y-6 p-6">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="text-2xl font-bold">OneIdol Admin</h1>
          <p className="text-sm text-muted-foreground">
            Panel kontrol platform — hanya untuk pemilik.
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="email" className="text-sm font-medium">
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="admin@oneidol.id"
            />
          </div>

          <div className="space-y-1.5">
            <label htmlFor="password" className="text-sm font-medium">
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {loading ? 'Masuk...' : 'Masuk'}
          </button>
        </form>
      </div>
    </div>
  )
}
